import { SelectOption } from '@shape-ds/components';
import { t } from 'i18next';
import { useGetCaseOptionsQuery } from 'queries/cases/queries';
import { caseOptions, CaseOptionsKeys } from 'queries/cases/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FormDatePicker, FormSelect } from 'ui/FormComponents';
import { formatOptionLabel } from 'utils';
import { FormComponentProps } from '../CaseFormStep/CaseFormStep';
import { useFormContext } from 'react-hook-form';
import { useFeatureFlag } from '@shape/feature-flag';
import { TextEditorWithLabel } from 'components/TextEditorWithLabel/TextEditorWithLabel';

const getTeamReview = () => {
  return [
    {
      label: t('case:team_review.rejected'),
      value: '0',
      disabled: false,
    },
    {
      label: t('case:team_review.accepted'),
      value: '1',
      disabled: false,
    },
  ];
};

const selectedKeys: CaseOptionsKeys[] = [
  caseOptions.severity,
  caseOptions.detection_method,
  caseOptions.dimension,
  caseOptions.discipline,
];

export function DiagnosisForm({
  isStepAble,
  isStepRequired,
  isCurrentStageSelected,
  onSubmit,
}: FormComponentProps) {
  const { t } = useTranslation();
  const diagnosisValidation = useFeatureFlag('diagnosis-validation');
  const { getValues } = useFormContext();
  const isCreatingCase = !getValues('id');

  const { caseId: paramCaseId } = useParams();

  const { data: caseOptions } = useGetCaseOptionsQuery();

  const teamReviewOptions = useMemo(getTeamReview, []);

  const groupedOptions = useMemo(() => {
    return selectedKeys.reduce((acc, key) => {
      const option = caseOptions?.[key] ?? [];

      acc[key] = Array.isArray(option)
        ? option.map((value: string) => ({
            value,
            label: formatOptionLabel(value),
            disabled: false,
          }))
        : [];

      return acc;
    }, {} as Record<CaseOptionsKeys, SelectOption[]>);
  }, [caseOptions, t]);

  const { severity, detection_method, dimension, discipline } = groupedOptions;
  const colSize = paramCaseId ? 'is-one-fifth' : 'is-one-third';

  return (
    <>
      <div className="columns is-multiline is-variable is-2">
        <div className={`column ${colSize}`}>
          <FormSelect
            name="severity"
            label={t('case:inputs.severity')}
            placeHolderText={t('case:placeholders.selectAnOption')}
            selectOptionData={severity}
            isRequired={isStepRequired}
            disabled={!isStepAble}
          />
        </div>

        <div className={`column ${colSize}`}>
          <FormSelect
            name="dimension"
            label={t('case:inputs.dimension')}
            placeHolderText={t('case:placeholders.selectAnOption')}
            selectOptionData={dimension}
            isRequired={isStepRequired}
            disabled={!isStepAble}
          />
        </div>

        <div className={`column ${colSize}`}>
          <FormSelect
            name="discipline"
            label={t('case:inputs.discipline')}
            placeHolderText={t('case:placeholders.selectAnOption')}
            selectOptionData={discipline}
            isRequired={isCurrentStageSelected}
            disabled={!isStepAble}
          />
        </div>

        <div className={`column ${colSize}`}>
          <FormDatePicker
            name="anomaly_start"
            label={t('case:inputs.anomaly_start')}
            dateFormat={t('components:date_filter.date.lts')}
            isRequired={isStepRequired}
            disabled={!isStepAble}
          />
        </div>

        <div className={`column ${colSize}`}>
          <FormSelect
            name="detection_method"
            label={t('case:inputs.detection_method')}
            placeHolderText={t('case:placeholders.selectAnOption')}
            selectOptionData={detection_method}
            isRequired={isStepRequired}
            disabled={!isStepAble}
          />
        </div>

        <div className="column is-one-third"></div>
      </div>

      <div className="columns">
        <div className="column is-full">
          <TextEditorWithLabel
            name="diagnosis"
            label={t('case:inputs.diagnosis')}
            isRequired={!isCreatingCase && isStepRequired}
            isDisabled={!isStepAble}
            placeholder={t('case:placeholders.diagnosis')}
            data-testid="diagnosis"
            onSubmit={onSubmit}
            isModal={isCreatingCase}
          />
        </div>
      </div>

      {diagnosisValidation && (
        <>
          <div className="columns">
            <div className="column">
              <span className="mr-2">{t('case:title.diagnosisValidation')}</span>
            </div>
          </div>

          <div className="columns is-variable is-2">
            <div className="column is-half">
              <FormSelect
                name="team_a_review"
                isRequired={isStepRequired}
                disabled={!isStepAble}
                label={t('case:inputs.team_a_review')}
                placeHolderText={t('case:placeholders.awaitingDiagnoseClosure')}
                selectOptionData={teamReviewOptions}
              />
            </div>
            <div className="column is-half">
              <FormSelect
                name="team_b_review"
                isRequired={isStepRequired}
                disabled={!isStepAble}
                label={t('case:inputs.team_b_review')}
                placeHolderText={t('case:placeholders.awaitingDiagnoseClosure')}
                selectOptionData={teamReviewOptions}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}
